import {
  AppBar,
  Box,
  IconButton,
  Toolbar,
  Icon,
  Tooltip,
  SxProps,
  Theme,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { ROUTES } from 'routing/routes';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { useAuthState } from 'shared/stores';
import { useCallback, useLayoutEffect } from 'react';
import MenuIcon from '@mui/icons-material/Menu';
import GroupsIcon from '@mui/icons-material/Groups';
import RateReviewIcon from '@mui/icons-material/RateReview';
import InsertDriveFile from '@mui/icons-material/InsertDriveFile';
import React from 'react';
import { ABGLogo } from 'shared/logos';
import { useUserPermissions } from 'shared/hooks/use-user-permissions';
import { PERMISSIONS } from 'shared/constants';

const userStyles: SxProps<Theme> = {
  width: 350,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const iconStyles: SxProps<Theme> = {
  color: 'common.white',
};

const logoStyles: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
};

const drawerWidth = '17rem';

export function Header() {
  const userPermissions = useUserPermissions();
  const hasControllerPermission = userPermissions.includes('abg-retail.controller');

  const appBarRef = React.useRef<HTMLDivElement>(null);
  const [drawerToolbarHeight, setDrawerToolbarHeight] = React.useState('104px');
  const { instance } = useMsal();
  const authenticated = useIsAuthenticated();
  const authState = useAuthState();
  const [isMenuDrawerOpen, setIsMenuDrawerOpen] = React.useState(false);
  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);
  const [isInitialOpeningClick, setIsInitialOpeningClick] = React.useState(true);

  const toggleMenuDrawer = useCallback(() => {
    setIsMenuDrawerOpen(!isMenuDrawerOpen);
  }, [setIsMenuDrawerOpen, isMenuDrawerOpen]);

  React.useEffect(() => {
    function handleResize() {
      setWindowWidth(window.innerWidth);
    }
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });

  React.useEffect(() => {
    function closeMenuDrawer() {
      if (isMenuDrawerOpen && !isInitialOpeningClick) {
        toggleMenuDrawer();
        setIsInitialOpeningClick(true);
      } else if (!isMenuDrawerOpen) {
        setIsInitialOpeningClick(true);
      } else {
        setIsInitialOpeningClick(false);
      }
    }
    window.addEventListener('click', closeMenuDrawer);

    return () => {
      window.removeEventListener('click', closeMenuDrawer);
    };
  }, [isMenuDrawerOpen, isInitialOpeningClick, setIsInitialOpeningClick, toggleMenuDrawer]);

  useLayoutEffect(() => {
    if (appBarRef.current) setDrawerToolbarHeight(`${appBarRef.current.offsetHeight}px`);
  }, [appBarRef, setDrawerToolbarHeight, windowWidth, appBarRef?.current?.offsetHeight]);

  const logout = useCallback(() => {
    instance.logout();
  }, [instance]);

  return (
    <>
      <div id='app-bar' ref={appBarRef}>
        <AppBar position='relative' sx={{ zIndex: '1251' }}>
          <Toolbar sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)' }}>
            <Box justifySelf='start'>
              {!hasControllerPermission && (
                <IconButton
                  color='inherit'
                  aria-label='open drawer'
                  onClick={toggleMenuDrawer}
                  edge='start'
                  sx={{ mr: 2 }}
                >
                  <MenuIcon />
                </IconButton>
              )}
            </Box>
            <Box justifySelf='center'>
              <Link to={ROUTES.demo}>
                <Box sx={logoStyles}>
                  <ABGLogo />
                </Box>
              </Link>
            </Box>
            <Box justifySelf='end'>
              {authenticated && (
                <Box sx={userStyles}>
                  User: {authState.user.get()?.displayName}
                  <Tooltip title='Logout'>
                    <IconButton onClick={logout}>
                      <Icon sx={iconStyles}>logout</Icon>
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
          </Toolbar>
        </AppBar>
      </div>
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
          zIndex: '1250',
        }}
        variant='persistent'
        anchor='left'
        open={isMenuDrawerOpen}
      >
        <Toolbar sx={{ height: drawerToolbarHeight }} />
        <Box sx={{ overflow: 'auto' }}>
          <List>
            {userPermissions.includes(PERMISSIONS['customer.view']) && (
              <ListItem button key={'customerManagementLink'} component={Link} to='/customer-management'>
                <ListItemIcon>
                  <GroupsIcon />
                </ListItemIcon>
                <ListItemText primary={'Customer Management'} />
              </ListItem>
            )}
            {userPermissions.includes(PERMISSIONS['contract.review']) && (
              <ListItem button key={'approvalWorkflowLink'} component={Link} to='/approval-workflow/contractReview'>
                <ListItemIcon>
                  <RateReviewIcon />
                </ListItemIcon>
                <ListItemText primary={'Approval Workflow'} />
              </ListItem>
            )}
            {userPermissions.includes(PERMISSIONS['quoting.view']) && (
              <ListItem button key={'quotingLink'} component={Link} to='/quoting'>
                <ListItemIcon>
                  <InsertDriveFile />
                </ListItemIcon>
                <ListItemText primary={'Quoting'} />
              </ListItem>
            )}
          </List>
        </Box>
      </Drawer>
    </>
  );
}
